import type {
  PriorityPositionOption,
  PrioritySectorOption
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

/** Formato das `options` do `SelectFilter`. */
export type SelectOption = { label: string; value: string }

/** Prefixo do valor de um id de posição selecionado que não veio nas opções. */
const ORPHAN_POSITION_PREFIX = '#'

const sectorLabel = (sector: PrioritySectorOption, withUnit: boolean): string => {
  const base = sector.archived ? `${sector.name} (arquivado)` : sector.name
  return withUnit && sector.healthUnitName ? `${base} · ${sector.healthUnitName}` : base
}

/**
 * Combo "Setor" (Q36): com unidade(s) escolhida(s), só os setores delas. Sem
 * unidade, todos — com o nome da unidade, porque "Pronto-socorro" se repete.
 * Com UMA unidade o sufixo é ruído. Um id selecionado que não está nas opções
 * (link antigo, setor removido) continua listado para poder ser desmarcado.
 */
export const buildSectorSelectOptions = (
  sectors: readonly PrioritySectorOption[],
  healthUnits: readonly number[],
  selected: readonly number[]
): SelectOption[] => {
  const visible = healthUnits.length
    ? sectors.filter((sector) => healthUnits.includes(sector.healthUnitId))
    : sectors
  const withUnit = healthUnits.length !== 1
  const known = new Set(sectors.map((sector) => sector.id))
  return [
    ...visible.map((sector) => ({
      label: sectorLabel(sector, withUnit),
      value: String(sector.id)
    })),
    ...selected
      .filter((id) => !known.has(id))
      .map((id) => ({ label: `Setor #${id}`, value: String(id) }))
  ]
}

/**
 * Poda o setor cuja unidade não está mais escolhida (Review Focus 1). Sem
 * unidade escolhida não há restrição. Id desconhecido fica (as opções podem
 * ainda não ter chegado). Devolve o MESMO array quando nada muda, para quem
 * chama poder comparar por referência e não emitir patch à toa.
 */
export const prunedSectorsForUnits = (
  sectorIds: number[],
  healthUnits: readonly number[],
  sectors: readonly PrioritySectorOption[]
): number[] => {
  if (!healthUnits.length || !sectorIds.length) return sectorIds
  const unitOf = new Map(sectors.map((sector) => [sector.id, sector.healthUnitId]))
  const kept = sectorIds.filter((id) => {
    const unit = unitOf.get(id)
    return unit === undefined || healthUnits.includes(unit)
  })
  return kept.length === sectorIds.length ? sectorIds : kept
}

const coveredIds = (positions: readonly PriorityPositionOption[]): Set<number> =>
  new Set(positions.flatMap((position) => position.positionIds))

/** Combo "Posição": uma opção por nome (o BE já agrupou); órfãos no fim. */
export const buildPositionSelectOptions = (
  positions: readonly PriorityPositionOption[],
  selected: readonly number[]
): SelectOption[] => {
  const covered = coveredIds(positions)
  return [
    ...positions.map((position) => ({ label: position.name, value: position.name })),
    ...selected
      .filter((id) => !covered.has(id))
      .map((id) => ({ label: `Posição #${id}`, value: `${ORPHAN_POSITION_PREFIX}${id}` }))
  ]
}

/**
 * Valores marcados no combo: o nome fica marcado se ALGUM id do grupo estiver
 * selecionado (seleção parcial também aparece marcada, para poder desligar o
 * grupo inteiro com um clique). Ids fora de qualquer grupo continuam como
 * órfãos `#id`.
 */
export const selectedPositionValues = (
  positions: readonly PriorityPositionOption[],
  selected: readonly number[]
): string[] => {
  const covered = coveredIds(positions)
  return [
    ...positions
      .filter((position) => position.positionIds.some((id) => selected.includes(id)))
      .map((position) => position.name),
    ...selected.filter((id) => !covered.has(id)).map((id) => `${ORPHAN_POSITION_PREFIX}${id}`)
  ]
}

/**
 * Liga/desliga um grupo inteiro: se todos já estão na lista, tira o grupo;
 * senão, acrescenta os que faltam. Usado pela barra de filtros (Situação,
 * "Todos os pendentes" do Estado do SLA). O combo de Posição NÃO usa esta função:
 * `togglePositionOption` tem semântica própria (`some`).
 */
export const toggleGroup = <T>(current: T[], group: readonly T[]): T[] =>
  group.every((item) => current.includes(item))
    ? current.filter((item) => !group.includes(item))
    : [...current, ...group.filter((item) => !current.includes(item))]

/**
 * Liga/desliga um nome inteiro (todos os ids) ou um id órfão. Um nome com
 * seleção PARCIAL (nem todos os ids, mas pelo menos um) é tratado como "já
 * marcado" (ver `selectedPositionValues`), então o clique desliga o grupo
 * inteiro em vez de ligá-lo — senão o usuário nunca conseguiria desmarcar uma
 * seleção parcial pelo combo.
 *
 * O ramo de nome é checado ANTES do ramo de órfão: um nome de posição real que
 * começa com `#` (texto livre, ex.: "#1 Plantonista") não pode cair no
 * caminho de id órfão e virar `NaN`. Só é tratado como órfão quando não há
 * grupo com esse nome E o sufixo após `#` é um inteiro positivo — um valor
 * com prefixo `#` que não é nem um nome de grupo nem um id válido é ignorado
 * (devolve `selected` inalterado).
 */
export const togglePositionOption = (
  positions: readonly PriorityPositionOption[],
  selected: number[],
  value: string
): number[] => {
  const group = positions.find((position) => position.name === value)?.positionIds
  if (group) {
    if (!group.length) return selected
    return group.some((id) => selected.includes(id))
      ? selected.filter((id) => !group.includes(id))
      : [...selected, ...group.filter((id) => !selected.includes(id))]
  }
  if (value.startsWith(ORPHAN_POSITION_PREFIX)) {
    const id = Number(value.slice(ORPHAN_POSITION_PREFIX.length))
    if (!Number.isInteger(id) || id <= 0) return selected
    return selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]
  }
  return selected
}
